import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Query,
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subscription, take } from 'rxjs';

import { BrowseTypesEnum } from '../../models/browse-types.enum';
import {
  BrowseFilter,
  FilterNamesEnum,
  getAllFiltersInSearchMode,
  getFiltersCopy,
} from '../../models/filter';
import { FilterStore } from '../../store/filter/filter.store';
import { SearchedTermStore } from '../../store/searched-term/searched-term.store';
import { SortingStore } from '../../store/sorting/sorting.store';
import {
  BrowseSorting,
  SortingValuesEnum,
  getSortings,
} from '../../models/sorting';
import { SearchService } from '../../services/search.service';
import { SearchMapComponent } from './search-map/search-map.component';
import { ListingComponent } from '../../components/listing/listing.component';
import { SearchToolbarComponent } from './search-toolbar/search-toolbar.component';
import { WORLD_REGIONS, WorldRegion } from '../../models/world-region';
import { TagStore } from '../../store/tag/tag.store';
import { Tag } from '../../models/tag.interface';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  standalone: true,
  imports: [SearchToolbarComponent, ListingComponent, SearchMapComponent],
})
export class SearchComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private searchService: SearchService = inject(SearchService);
  private searchedTermStore: SearchedTermStore = inject(SearchedTermStore);
  private filterStore: FilterStore = inject(FilterStore);
  private sortingStore: SortingStore = inject(SortingStore);
  private tagStore: TagStore = inject(TagStore);
  private subscriptions: Subscription[] = [];

  ContentTypes: typeof BrowseTypesEnum = BrowseTypesEnum;
  searchedContentType: BrowseTypesEnum = BrowseTypesEnum.Video;
  searchDisplay: 'list' | 'map' = 'list';
  top: string = 'calc(35px + 2em)';
  actualScrollTop: number = 0;
  navbarHide: boolean = false;

  @HostListener('window:mousemove', ['$event'])
  onMouseMove({ clientY }: MouseEvent): void {
    if (clientY <= 65) {
      this.top = 'calc(35px + 2em)';
    }
  }

  @HostListener('window:scroll', ['$event'])
  onScroll({ target }: { target: Document }): void {
    const scrollTop: number = target.scrollingElement.scrollTop;
    this.top = scrollTop >= this.actualScrollTop ? '0' : 'calc(35px + 2em)';
    this.actualScrollTop = scrollTop;
  }

  private resetFiltersAndSorting(): void {
    this.filterStore.setSearchedContentFilters(
      getAllFiltersInSearchMode(
        getFiltersCopy().find(
          (f: BrowseFilter) => f.name === FilterNamesEnum.SearchMode
        )
      )
    );
    this.sortingStore.setSearchedContentSorting(
      getSortings().find(
        (s: BrowseSorting) => s.value === SortingValuesEnum.Recent
      )
    );
  }

  ngOnInit(): void {
    this.searchService.subscribeToFetch();
    this.subscriptions.push(
      // si tu as un terme a chercher alors tu le cherches !
      this.route.params.subscribe((res: any) => {
        if (res.term) this.searchedTermStore.setSearchedTerm(res.term);
      }),

      this.route.queryParamMap.subscribe((paramMap: ParamMap) => {
        const query: ParamMap = paramMap['params'];

        if (query['country']) {
          const browseFilter: BrowseFilter = {
            name: FilterNamesEnum.Region,
            label: 'browse.filterSort.filters.region.title',
            selectedOptions: [
              {
                value:
                  WORLD_REGIONS.find(
                    (region: WorldRegion) => region.name === query['country']
                  )?.code.toString() || '',
                label: query['country'],
              },
            ],
          };
          this.filterStore.searchedContentFilters$
            .pipe(take(1))
            .subscribe((filters: BrowseFilter[]) => {
              const existingFilterIndex: number = filters.findIndex(
                (filter: BrowseFilter) => filter.name === FilterNamesEnum.Region
              );
              if (existingFilterIndex !== -1) {
                const updatedFilters: BrowseFilter[] = [...filters];
                updatedFilters[existingFilterIndex] = browseFilter;
                this.filterStore.setSearchedContentFilters(updatedFilters);
              } else {
                console.error('Filter not found');
              }
            });
        }

        if (query['tag']) {
          const tagId: string = query['tag'];
          this.tagStore.tags$.pipe(take(1)).subscribe((tags: Tag[]) => {
            const foundTag: Tag = tags.find(
              (tag: Tag) => tag.id.toString() === tagId
            );
            const tagLabel: string = foundTag ? foundTag.name : '';
            const browseFilter: BrowseFilter = {
              name: FilterNamesEnum.Tag,
              label: 'browse.filterSort.filters.tag.title',
              selectedOptions: [
                {
                  value: tagId,
                  label: tagLabel,
                },
              ],
            };

            this.filterStore.searchedContentFilters$
              .pipe(take(1))
              .subscribe((filters: BrowseFilter[]) => {
                const existingFilterIndex: number = filters.findIndex(
                  (filter: BrowseFilter) => filter.name === FilterNamesEnum.Tag
                );
                if (existingFilterIndex !== -1) {
                  const updatedFilters: BrowseFilter[] = [...filters];
                  updatedFilters[existingFilterIndex] = browseFilter;
                  this.filterStore.setSearchedContentFilters(updatedFilters);
                } else {
                  console.error('Filter not found');
                }
              });
          });
        }
      }),

      this.filterStore.searchedContentFilters$.subscribe(
        (filters: BrowseFilter[]) => {
          const modeFilter: BrowseFilter = filters.find(
            (filter: BrowseFilter) => filter.name === FilterNamesEnum.SearchMode
          );

          this.searchedContentType = modeFilter.selectedOptions[0]
            .value as BrowseTypesEnum;
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.searchService.unsubscribeToFetch();
    this.subscriptions.forEach((sub: Subscription) => {
      sub.unsubscribe();
    });
    this.resetFiltersAndSorting();
  }
}
